import React from "react";
import { Link } from "react-router-dom";
import { PublicNav, PublicFooter } from "@/components/public/PublicChrome";
import { Calculator, CalendarClock, Boxes, Workflow, Factory, Headset, ArrowRight, CheckCircle2 } from "lucide-react";

const flow = ["Booking","Appraisal","Quotation","Design","3D Printing","QC","Finishing","Packing","Delivery"];
const services = [
  { icon: Calculator, t: "Instant Quote Calculator", d: "Upload file STL/OBJ, lihat estimasi berat, waktu cetak & harga secara real-time." },
  { icon: CalendarClock, t: "Booking Konsultasi", d: "Jadwalkan kunjungan dengan slot 30 menit, konfirmasi instan ala Google Reservation." },
  { icon: Factory, t: "Produksi End-to-End", d: "Dari design, 3D print, QC, finishing, assembly, packing hingga delivery." },
  { icon: Workflow, t: "Work Order & Project", d: "Satu Work Order jadi root data: task, employee, stock, cost, KPI sampai payroll." },
  { icon: Boxes, t: "Stock & Purchasing", d: "Reservasi material otomatis, stock ledger, dan purchase request saat kekurangan." },
  { icon: Headset, t: "CRM & Event", d: "Kelola customer, project event / field, dan histori komunikasi dalam satu data." },
];

export default function Landing() {
  return (
    <div>
      <PublicNav />
      {/* Hero */}
      <section className="relative overflow-hidden grid-bg">
        <div className="max-w-7xl mx-auto px-5 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="anim-up">
            <div className="inline-flex items-center gap-2 text-xs font-mono bg-white border border-slate-200 rounded-full px-3 py-1 text-sky-600">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> ONE DATA · SINGLE SOURCE OF TRUTH
            </div>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mt-5">
              All-in-One<br/>Sistem <span className="text-sky-500">3D Printing</span>
            </h1>
            <p className="text-slate-600 text-base sm:text-lg mt-5 max-w-lg">
              Dari booking, design, produksi, finishing, project sampai event — semua terhubung dalam satu platform operasional.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/calculator" data-testid="hero-calc-btn" className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3.5 rounded-xl font-medium hover:bg-slate-800 transition-colors">
                <Calculator className="w-5 h-5" /> Hitung Estimasi
              </Link>
              <Link to="/booking" data-testid="hero-booking-btn" className="inline-flex items-center gap-2 bg-white border border-slate-300 px-6 py-3.5 rounded-xl font-medium hover:border-slate-900 transition-colors">
                <CalendarClock className="w-5 h-5" /> Booking Kunjungan
              </Link>
            </div>
          </div>
          <div className="relative anim-up" style={{ animationDelay: ".1s" }}>
            <img src="https://images.unsplash.com/photo-1611117775350-ac3950990985?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="3D printing factory" className="rounded-2xl border border-slate-200 shadow-xl w-full object-cover aspect-[4/3]" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-xl border border-slate-200 shadow-lg p-4 hidden sm:block">
              <div className="text-xs text-slate-500 font-mono">PRODUCTION FLOW</div>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs font-medium">
                Design <ArrowRight className="w-3 h-3 text-slate-400" /> Print <ArrowRight className="w-3 h-3 text-slate-400" /> QC <ArrowRight className="w-3 h-3 text-slate-400" /> Delivery
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flow strip */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 py-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm">
          {flow.map((f, i) => (
            <React.Fragment key={f}>
              <span className="font-medium text-slate-700">{f}</span>
              {i < flow.length - 1 && <ArrowRight className="w-4 h-4 text-sky-400" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-7xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Satu platform, seluruh operasional.</h2>
          <p className="text-slate-600 mt-3">Bukan kumpulan aplikasi terpisah — setiap data dapat ditelusuri dari customer hingga profit project.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {services.map((s, i) => (
            <div key={s.t} data-testid={`service-card-${i}`} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center"><s.icon className="w-5 h-5" /></div>
              <h3 className="font-head text-lg font-medium mt-4">{s.t}</h3>
              <p className="text-sm text-slate-600 mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 pb-6">
        <div className="bg-slate-900 rounded-3xl p-10 sm:p-14 text-white grid-bg relative overflow-hidden">
          <h2 className="font-head text-2xl sm:text-3xl font-semibold max-w-lg">Siap mulai project 3D printing Anda?</h2>
          <p className="text-slate-400 mt-3 max-w-md">Dapatkan estimasi harga instan atau jadwalkan konsultasi dengan tim kami.</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/calculator" className="inline-flex items-center gap-2 bg-sky-500 px-6 py-3 rounded-xl font-medium hover:bg-sky-400 transition-colors">Mulai Kalkulator <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/booking" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-6 py-3 rounded-xl font-medium hover:bg-white/20 transition-colors">Booking Konsultasi</Link>
          </div>
          <div className="flex flex-wrap gap-5 mt-8 text-sm text-slate-300">
            {["Estimasi real-time","Slot booking 30 menit","Kirim quote via WhatsApp"].map(x => (
              <span key={x} className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> {x}</span>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
